import * as React from "react";

import { Collapsible } from "@replohq/lattice/components/collapsible/Collapsible";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replohq/lattice/components/menu/Menu";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@replohq/lattice/components/sidebar/Sidebar";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import {
  ArrowUpRight,
  ChevronRight,
  FileText,
  GraduationCap,
  LibraryBig,
  Megaphone,
  MessageSquareShare,
  ThumbsUp,
  UserCheck,
} from "lucide-react";

const CHANGELOG_URL = "https://www.replo.app/changelog";

type ResourceLink = {
  title: string;
  href: string;
  icon: React.ReactNode;
};

function getResourceLinks(hostingType: string): ResourceLink[] {
  if (hostingType === "replo") {
    return [
      {
        title: "Help Docs",
        href: "https://docs.replo.app",
        icon: <FileText size={16} />,
      },
      {
        title: "Join the Discord",
        href: "https://discord.gg/E7K6VADQJM",
        icon: <MessageSquareShare size={16} />,
      },
      {
        title: "Feedback",
        href: "https://feedback.replo.app/?utm_source=dashboard",
        icon: <ThumbsUp size={16} />,
      },
    ];
  }

  return [
    {
      title: "Reploversity",
      href: "https://www.replo.app/learn",
      icon: <GraduationCap size={16} />,
    },
    {
      title: "Help Docs",
      href: "https://support.replo.app/",
      icon: <FileText size={16} />,
    },
    {
      title: "Replo Experts",
      href: "https://contra.com/replo",
      icon: <UserCheck size={16} />,
    },
    {
      title: "Join the Discord",
      href: "https://discord.gg/E7K6VADQJM",
      icon: <MessageSquareShare size={16} />,
    },
    {
      title: "Feedback",
      href: "https://feedback.replo.app/?utm_source=dashboard",
      icon: <ThumbsUp size={16} />,
    },
  ];
}

const WhatsNewSubButton = () => {
  return (
    <SidebarMenuSubButton
      href={CHANGELOG_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Megaphone />
      <span>What&apos;s New</span>
      <ArrowUpRight className="ml-auto shrink-0 text-placeholder!" />
    </SidebarMenuSubButton>
  );
};

const LearnMoreCollapsible = ({
  resourceLinks,
}: {
  resourceLinks: ResourceLink[];
}) => {
  return (
    <SidebarMenuItem>
      <Collapsible
        trigger={
          <SidebarMenuButton tooltip="Learn More">
            <LibraryBig />
            <span>Learn More</span>
            <ChevronRight className="ml-auto text-placeholder transition-transform [[data-panel-open]_&]:rotate-90" />
          </SidebarMenuButton>
        }
      >
        <SidebarMenuSub>
          <SidebarMenuSubItem>
            <WhatsNewSubButton />
            {resourceLinks.map((link) => (
              <SidebarMenuSubButton
                key={link.title}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.icon}
                <span>{link.title}</span>
                <ArrowUpRight className="ml-auto shrink-0 text-placeholder!" />
              </SidebarMenuSubButton>
            ))}
          </SidebarMenuSubItem>
        </SidebarMenuSub>
      </Collapsible>
    </SidebarMenuItem>
  );
};

const LearnMoreMenu = ({
  resourceLinks,
}: {
  resourceLinks: ResourceLink[];
}) => {
  return (
    <SidebarMenuItem>
      <Menu>
        <MenuTrigger className="w-full">
          <SidebarMenuButton tooltip="Learn More">
            <LibraryBig />
          </SidebarMenuButton>
        </MenuTrigger>
        <MenuContent side="right" sideOffset={16} align="end">
          <MenuItem
            label="What's New"
            startEnhancer={<Megaphone size={14} />}
            endEnhancer={
              <ArrowUpRight size={14} className="text-placeholder" />
            }
            onClick={() => {
              window.open(CHANGELOG_URL, "_blank", "noopener,noreferrer");
            }}
          />
          {resourceLinks.map((link) => (
            <MenuItem
              key={link.title}
              label={link.title}
              startEnhancer={link.icon}
              endEnhancer={<ArrowUpRight className="text-placeholder" />}
              onClick={() => {
                window.open(link.href, "_blank", "noopener,noreferrer");
              }}
            />
          ))}
        </MenuContent>
      </Menu>
    </SidebarMenuItem>
  );
};

const LearnMoreCollapsibleNavItem = () => {
  const { project } = useCurrentProjectContext();
  const resourceLinks = getResourceLinks(project?.hostingType ?? "replo");
  const { open } = useSidebar();

  if (!open) {
    return <LearnMoreMenu resourceLinks={resourceLinks} />;
  }

  return <LearnMoreCollapsible resourceLinks={resourceLinks} />;
};

export default LearnMoreCollapsibleNavItem;
